import { useEffect, useState, useMemo } from 'react';

import {
  getInsightUserAccess,
  updateInsightClientUserAccess,
} from 'api/generated/user-insight-access/user-insight-access';
import { updateClientInsight } from 'api/generated/client-insights/client-insights';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export interface AccessUser {
  userId: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  hasAccess: boolean;
  downloaded?: boolean;
  viewed?: boolean;
  [key: string]: unknown;
}

interface UseInsightAccessInsight {
  id?: string | number;
  autoAssignToUsers?: boolean;
}

interface UseInsightAccessArgs {
  insight: UseInsightAccessInsight | null | undefined;
  clientId: string | number | undefined;
}

interface UseInsightAccessResult {
  loading: boolean;
  saving: boolean;
  displayedData: AccessUser[];
  isAllUsersSelected: boolean;
  isIndeterminate: boolean;
  isToggleOn: boolean;
  hasChanges: boolean;
  onSelectAllUsers: (args: { hasAccess: boolean }) => void;
  onUserChange: (changedUser: AccessUser) => void;
  onToggleChange: (toggled: boolean) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
}

const autoAssignCache = new Map<string | number, boolean>();

export const useInsightAccess = ({
  insight,
  clientId,
}: UseInsightAccessArgs): UseInsightAccessResult => {
  const currentUser = useCurrentUser();

  const userId = currentUser?.data?.oktaUserId;
  const insightId = insight?.id;

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [displayedData, setDisplayedData] = useState<AccessUser[]>([]);
  const [originalData, setOriginalData] = useState<AccessUser[]>([]);
  const [isAllUsersSelected, setIsAllUsersSelected] = useState<boolean>(false);
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false);

  const getInitialAutoAssign = (): boolean => {
    if (insightId !== undefined && autoAssignCache.has(insightId)) {
      return autoAssignCache.get(insightId) || false;
    }

    return insight?.autoAssignToUsers || false;
  };

  const [isToggleOn, setIsToggleOn] = useState<boolean>(getInitialAutoAssign());
  const [originalToggle, setOriginalToggle] = useState<boolean>(
    getInitialAutoAssign(),
  );

  const loadClientUsers = async (): Promise<void> => {
    if (!userId || !insightId) {
      return;
    }

    setLoading(true);
    try {
      const rows =
        (await getInsightUserAccess(userId, Number(insightId), {
          size: 100,
        })) ?? [];

      const usersWithAccess: AccessUser[] = rows.flatMap((u) => {
        if (u.userId === undefined) {
          return [];
        }

        return [
          {
            userId: u.userId,
            firstName: u.firstName,
            lastName: u.lastName,
            jobTitle: u.jobTitle,
            hasAccess: u.hasAccess ?? false,
            downloaded: u.downloaded,
            viewed: u.viewed,
          },
        ];
      });

      const checkedCount = usersWithAccess.filter(
        (user) => user.hasAccess,
      ).length;
      const allChecked =
        usersWithAccess.length > 0 && checkedCount === usersWithAccess.length;
      const noneChecked = checkedCount === 0;

      setDisplayedData(usersWithAccess);
      setOriginalData(usersWithAccess);
      setIsAllUsersSelected(allChecked);
      setIsIndeterminate(!allChecked && !noneChecked);

      const autoAssign = insight?.autoAssignToUsers || false;

      setIsToggleOn(autoAssign);
      setOriginalToggle(autoAssign);
    } catch (error) {
      console.error('Error loading users:', error);
      setDisplayedData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  };

  const onSelectAllUsers = ({ hasAccess }: { hasAccess: boolean }): void => {
    const updatedUsers = displayedData.map((item) => ({
      ...item,
      hasAccess,
    }));

    setDisplayedData(updatedUsers);
    setIsAllUsersSelected(hasAccess);
    setIsIndeterminate(false);
  };

  const onUserChange = (changedUser: AccessUser): void => {
    const updatedUsers = displayedData.map((item) =>
      String(changedUser.userId) === String(item.userId)
        ? { ...item, hasAccess: changedUser.hasAccess }
        : item,
    );

    setDisplayedData(updatedUsers);

    const checkedCount = updatedUsers.filter((user) => user.hasAccess).length;
    const allChecked = checkedCount === updatedUsers.length;
    const noneChecked = checkedCount === 0;

    setIsAllUsersSelected(allChecked);
    setIsIndeterminate(!allChecked && !noneChecked);
  };

  const onToggleChange = (toggled: boolean): void => {
    setIsToggleOn(toggled);
  };

  const hasUserAccessChanges = useMemo<boolean>(() => {
    if (displayedData.length !== originalData.length) return true;

    return displayedData.some((user) => {
      const originalUser = originalData.find((u) => u.userId === user.userId);

      return !!originalUser && originalUser.hasAccess !== user.hasAccess;
    });
  }, [displayedData, originalData]);

  const hasChanges = useMemo<boolean>(
    () => isToggleOn !== originalToggle || hasUserAccessChanges,
    [isToggleOn, originalToggle, hasUserAccessChanges],
  );

  const handleSave = async (): Promise<void> => {
    if (!userId || !insightId || !clientId) return;

    setSaving(true);
    try {
      if (hasUserAccessChanges) {
        const userAccess = displayedData.map((user) => ({
          userId: Number(user.userId),
          hasAccess: user.hasAccess,
        }));

        await updateInsightClientUserAccess(userId, Number(insightId), userAccess);
      }

      if (isToggleOn !== originalToggle) {
        await updateClientInsight(userId, Number(clientId), Number(insightId), {
          autoAssignToUsers: isToggleOn,
        });

        autoAssignCache.set(insightId, isToggleOn);
      }

      setOriginalData(displayedData);
      setOriginalToggle(isToggleOn);
    } catch (error) {
      console.error('Error saving access changes:', error);

      await loadClientUsers();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    setDisplayedData(originalData);
    setIsToggleOn(originalToggle);

    const checkedCount = originalData.filter((user) => user.hasAccess).length;
    const allChecked =
      !!originalData.length && checkedCount === originalData.length;
    const noneChecked = !checkedCount;

    setIsAllUsersSelected(allChecked);
    setIsIndeterminate(!allChecked && !noneChecked);
  };

  useEffect(() => {
    if (userId && insightId) {
      loadClientUsers();
    }
  }, [userId, insightId]);

  return {
    loading,
    saving,
    displayedData,
    isAllUsersSelected,
    isIndeterminate,
    isToggleOn,
    hasChanges,
    onSelectAllUsers,
    onUserChange,
    onToggleChange,
    handleSave,
    handleCancel,
  };
};
